import { snapToPixel, lerp, clampRange } from '../util/math.js';
import { clamp01 } from '../util/math.js';
import { pointDistance } from '../util/interpolation.js';
import { fieldMetrics, screenXForYard, goalLineXForDirection, orientX } from '../field/metrics.js';
import { stopReasonForPlay, playHash } from './helpers.js';
import { clonePlayers, findPlayer, setPlayerState, setPlayersStill, carriedBallPoint } from '../player/model.js';
import { compactPathPoints, createPathSegment, createHoldSegment, evaluateTrack, createMotionState, steerMotionState, guidePointAt, flowingGuideTarget, projectedPoint, curvedApproachTarget } from './track.js';
import { liveFrameCount } from './context.js';
import { kickoffReturnLaneY, kickoffFinishY } from './waypoints.js';
import type { FieldPlayer } from '../player/model.js';
import { buildArcadeKickoffLiveSequence } from './live-kickoff-arcade.js';

function seedMotionState(
  role: string,
  basePlayers: FieldPlayer[],
  tracks: Map<string, any[]>,
  frame: number,
): any | null {
  const basePlayer = findPlayer(basePlayers, role);
  if (!basePlayer) {
    return null;
  }
  const current = evaluateTrack(tracks.get(role) ?? null, frame, basePlayer);
  const previous = evaluateTrack(tracks.get(role) ?? null, Math.max(0, frame - 1), basePlayer);
  const state = createMotionState({ ...basePlayer, x: current.x, y: current.y });
  state.vx = current.x - previous.x;
  state.vy = current.y - previous.y;
  return state;
}

function blendPoints(a: any, b: any, amount: number) {
  return {
    x: snapToPixel(lerp(a.x, b.x, clamp01(amount))),
    y: snapToPixel(lerp(a.y, b.y, clamp01(amount))),
  };
}

function diminishingBounceLift(progress: number) {
  const bounceProgress = clamp01(progress / 0.44);
  if (bounceProgress >= 1) {
    return 0;
  }
  return Math.sin(bounceProgress * Math.PI) * 18;
}

export function buildKickoffLiveSequenceV4(context: any): any[] {
  if (context.play?.arcadeReplay?.source === 'arcade-sim-v1' && context.play?.arcadePlaybackMode === 'raw') {
    return buildArcadeKickoffLiveSequence(context);
  }
  const frameCount = liveFrameCount(context.play);
  const stopReason = stopReasonForPlay(context.play);
  const kickoffOutcome = context.play.kickoffOutcome || "";
  const onsideKick = kickoffOutcome === "onside-recovered" || kickoffOutcome === "onside-failed";
  const touchback = stopReason === "touchback";
  const deadShort = stopReason === "dead-short";
  const outOfBounds = stopReason === "out-of-bounds";
  const touchdown = stopReason === "touchdown";
  const tackledReturn = stopReason === "tackle";
  const noReturn = touchback || deadShort || onsideKick;
  const offenseBase = clonePlayers(context.formation.offense);
  const defenseBase = clonePlayers(context.formation.defense);
  const metrics = fieldMetrics();
  const xDelta = (value: number) => value * context.direction;
  const returnXDelta = (value: number) => value * -context.direction;
  const forwardReturnX = (baseX: number, targetX: number) => (returnXDelta(1) < 0 ? Math.min(baseX, targetX) : Math.max(baseX, targetX));
  const coverageAdvanceX = (baseX: number, targetX: number) => (xDelta(1) < 0 ? Math.min(baseX, targetX) : Math.max(baseX, targetX));
  const lane = clampRange(Math.round(context.play.returnLane ?? 0), -2, 2);
  const laneBias = lane === 0 ? (playHash(context.play, "kickoff-bias") % 2 === 0 ? 1 : -1) : Math.sign(lane);
  const coverageRoles = Array.from({ length: 10 }, (_, index) => "COVER" + (index + 1));
  const frontRoles = Array.from({ length: 5 }, (_, index) => "FRONT" + (index + 1));
  const wallRoles = Array.from({ length: 4 }, (_, index) => "WALL" + (index + 1));
  const frontOffsets = [-110, -56, 0, 56, 110];
  const wallOffsets = [-84, -28, 28, 84];
  const activeReturnerRole = lane > 0 ? "RETURNER2" : "RETURNER1";
  const decoyReturnerRole = activeReturnerRole === "RETURNER1" ? "RETURNER2" : "RETURNER1";
  const activeReturnerBase = findPlayer(defenseBase, activeReturnerRole) || findPlayer(defenseBase, "RETURNER1") || defenseBase[0];
  const decoyReturnerBase = findPlayer(defenseBase, decoyReturnerRole) || activeReturnerBase;
  const kickerBase = findPlayer(offenseBase, "KICKER") || { x: screenXForYard(35, context.direction), y: context.centerY - 4 };
  const landingYard = onsideKick
    ? Math.max(44, Math.min(50, context.play.landingYardLine ?? 46))
    : Math.max(74, Math.min(100, context.play.landingYardLine ?? 95));
  const fieldedYard = onsideKick
    ? Math.max(44, Math.min(50, context.play.fieldedYardLine ?? landingYard))
    : Math.max(80, Math.min(100, context.play.fieldedYardLine ?? landingYard));
  const fieldedX = screenXForYard(fieldedYard, context.direction);
  const deadBallX = screenXForYard(context.play.deadBallYardLine ?? landingYard, context.direction);
  const targetLaneY = kickoffReturnLaneY(context.play, context.centerY);
  const catchPoint = { x: fieldedX, y: activeReturnerBase.y };
  const returnSpot = { x: context.endX, y: kickoffFinishY(context.play, context.centerY) };
  const goalLineX = goalLineXForDirection(context.direction);
  const directTouchbackCatchPoint = {
    x: orientX(goalLineX, 42, context.direction),
    y: clampRange(
      snapToPixel(lerp(activeReturnerBase.y, targetLaneY, 0.14)),
      metrics.fieldTop + 20,
      metrics.fieldBottom - 20
    ),
  };
  const directTouchbackKneelPoint = {
    x: orientX(goalLineX, 34, context.direction),
    y: clampRange(
      snapToPixel(directTouchbackCatchPoint.y + laneBias * 2),
      metrics.fieldTop + 20,
      metrics.fieldBottom - 20
    ),
  };
  const rollTouchbackLaneY = clampRange(
    snapToPixel(metrics.fieldTop + 146 + ((playHash(context.play, "kickoff-roll-lane") % 3) - 1) * 8),
    metrics.fieldTop + 120,
    metrics.fieldTop + 164
  );
  const rollTouchbackExitY = clampRange(
    snapToPixel(metrics.fieldTop + 34 + ((playHash(context.play, "kickoff-roll-exit-y") % 3) - 1) * 6),
    metrics.fieldTop + 24,
    metrics.fieldTop + 46
  );
  const rollTouchbackBouncePoint = {
    x: orientX(goalLineX, 38 + (playHash(context.play, "kickoff-roll-depth") % 3) * 8, context.direction),
    y: rollTouchbackLaneY,
  };
  const rollTouchbackWatchPoint = {
    x: orientX(goalLineX, 26, context.direction),
    y: clampRange(
      snapToPixel(lerp(activeReturnerBase.y, rollTouchbackLaneY, 0.68) + 24),
      metrics.fieldTop + 20,
      metrics.fieldBottom - 20
    ),
  };
  const rollTouchbackExitPoint = {
    x: orientX(goalLineX, metrics.endZoneWidth + 10, context.direction),
    y: rollTouchbackExitY,
  };
  const rollTouchbackGuide = compactPathPoints([
    rollTouchbackBouncePoint,
    rollTouchbackExitPoint,
  ]);
  const returnDirection = Math.sign(returnSpot.x - catchPoint.x) || Math.sign(returnXDelta(1)) || 1;
  const settlePoint = {
    x: snapToPixel(fieldedX + returnXDelta(8)),
    y: snapToPixel(lerp(activeReturnerBase.y, targetLaneY, 0.34)),
  };
  const seamPoint = {
    x: forwardReturnX(
      settlePoint.x,
      snapToPixel(lerp(fieldedX, returnSpot.x, 0.28) + returnXDelta(12))
    ),
    y: clampRange(
      snapToPixel(lerp(settlePoint.y, targetLaneY, 0.72) + laneBias * (12 + Math.abs(lane) * 8)),
      metrics.fieldTop + 20,
      metrics.fieldBottom - 20
    ),
  };
  const alleyPoint = {
    x: forwardReturnX(seamPoint.x, snapToPixel(lerp(fieldedX, returnSpot.x, 0.62))),
    y: clampRange(
      snapToPixel(lerp(seamPoint.y, returnSpot.y, outOfBounds ? 0.72 : 0.52)),
      metrics.fieldTop + 20,
      metrics.fieldBottom - 20
    ),
  };
  const returnGuide = compactPathPoints([catchPoint, settlePoint, seamPoint, alleyPoint, returnSpot]);
  const coverageEntries = coverageRoles
    .map((role, index) => {
      const player = findPlayer(offenseBase, role);
      return player ? { role, index, player, distance: Math.abs(player.y - targetLaneY) } : null;
    })
    .filter(Boolean) as { role: string; index: number; player: any; distance: number }[];
  const focusEntries = [...coverageEntries].sort((left, right) => left.distance - right.distance);
  const primaryRole = focusEntries[0]?.role || "COVER6";
  const fitRole = focusEntries[1]?.role || primaryRole;
  const cleanupRole = focusEntries[2]?.role || fitRole;
  const highContainRole = coverageEntries
    .filter((entry) => entry.player.y < targetLaneY && ![primaryRole, fitRole, cleanupRole].includes(entry.role))
    .sort((left, right) => Math.abs(left.player.y - targetLaneY) - Math.abs(right.player.y - targetLaneY))[0]?.role || primaryRole;
  const lowContainRole = coverageEntries
    .filter((entry) => entry.player.y > targetLaneY && ![primaryRole, fitRole, cleanupRole].includes(entry.role))
    .sort((left, right) => Math.abs(left.player.y - targetLaneY) - Math.abs(right.player.y - targetLaneY))[0]?.role || cleanupRole;
  const breakthroughRoles = new Set<string>([
    primaryRole,
    fitRole,
    playHash(context.play, "kickoff-breakthrough-role") % 2 === 0 ? cleanupRole : (laneBias < 0 ? highContainRole : lowContainRole),
  ].filter(Boolean));
  const blockerAssignments = new Map();
  [...frontRoles, ...wallRoles]
    .map((role) => ({ role, player: findPlayer(defenseBase, role) }))
    .filter((entry) => entry.player)
    .sort((left, right) => left.player!.y - right.player!.y)
    .forEach((entry, index, entries) => {
      const coverageByY = [...coverageEntries].sort((left, right) => left.player.y - right.player.y);
      const coverageIndex = Math.round((index / Math.max(1, entries.length - 1)) * Math.max(0, coverageByY.length - 1));
      blockerAssignments.set(entry.role, coverageByY[coverageIndex] || null);
    });
  const coverageMatchByRole = new Map<string, string | null>();
  blockerAssignments.forEach((entry, blockerRole) => {
    if (entry?.role) {
      coverageMatchByRole.set(entry.role, blockerRole);
    }
  });

  const kickoffOrigin = { x: kickerBase.x + 6, y: kickerBase.y + 8 };
  const hangRatio = clampRange(((context.play.hangTime || 4.1) - 3.4) / 1.5, 0, 1);
  const hangFrames = onsideKick
    ? Math.max(28, Math.round((context.play.hangTime || 1.15) * 30))
    : Math.max(96, Math.round((context.play.hangTime || 4.1) * 30));
  const rollTouchback = kickoffOutcome === "landing-zone-roll-touchback";
  const noReturnTailFrames = rollTouchback ? 12 : 10;
  const totalFrames = Math.max(frameCount + (noReturn ? noReturnTailFrames : 24), hangFrames + (noReturn ? (rollTouchback ? 26 : 20) : 80));
  const lastFrame = totalFrames - 1;
  const flightEnd = Math.min(lastFrame - (noReturn ? 10 : 18), hangFrames);
  const releaseFrame = noReturn
    ? Math.min(lastFrame - 2, flightEnd + 2)
    : Math.min(lastFrame - 14, flightEnd + 2);
  const motionEnd = noReturn ? Math.min(lastFrame - 1, releaseFrame + (rollTouchback ? 12 : 8)) : lastFrame - 1;
  const offenseTracks = new Map();
  const defenseTracks = new Map();
  const addPath = (segments: any[], startFrame: number, endFrame: number, points: any[], moving = true) => {
    if (endFrame <= startFrame) {
      return;
    }
    segments.push(createPathSegment(startFrame, endFrame, points, "polyline", moving));
  };
  const addHold = (segments: any[], startFrame: number, endFrame: number, point: any, moving = false) => {
    if (endFrame < startFrame) {
      return;
    }
    segments.push(createHoldSegment(startFrame, endFrame, point, moving));
  };
  const trackPlayer = (tracks: Map<string, any[]>, role: string, basePoint: any, builder: (basePoint: any) => any[]) => {
    tracks.set(role, builder(basePoint).filter(Boolean));
  };
  const dynamicPlans = new Map<string, any>();

  trackPlayer(defenseTracks, activeReturnerRole, activeReturnerBase, (basePoint) => {
    const segments: any[] = [];

    if (kickoffOutcome === "touchback-35") {
      addPath(segments, 0, flightEnd, [basePoint, directTouchbackCatchPoint], true);
      addPath(segments, flightEnd + 1, motionEnd, [directTouchbackCatchPoint, directTouchbackKneelPoint], true);
      addHold(segments, motionEnd, lastFrame, directTouchbackKneelPoint, false);
      return segments;
    }

    if (deadShort) {
      addHold(segments, 0, lastFrame, basePoint, false);
      return segments;
    }

    if (onsideKick) {
      if (kickoffOutcome === "onside-failed") {
        addPath(segments, 0, flightEnd, [basePoint, catchPoint], true);
        addHold(segments, flightEnd, lastFrame, catchPoint, false);
      } else {
        addHold(segments, 0, lastFrame, basePoint, false);
      }
      return segments;
    }

    addPath(segments, 0, flightEnd, [basePoint, kickoffOutcome === "landing-zone-roll-touchback" ? rollTouchbackWatchPoint : catchPoint], true);

    if (kickoffOutcome === "landing-zone-roll-touchback") {
      const settlePoint = {
        x: orientX(goalLineX, 34, context.direction),
        y: clampRange(
          snapToPixel(rollTouchbackWatchPoint.y + 6),
          metrics.fieldTop + 20,
          metrics.fieldBottom - 20
        ),
      };
      addPath(segments, flightEnd + 1, motionEnd, [rollTouchbackWatchPoint, settlePoint], true);
      addHold(segments, motionEnd, lastFrame, settlePoint, false);
      return segments;
    }

    addPath(segments, flightEnd + 1, motionEnd, returnGuide, true);
    addHold(segments, motionEnd, lastFrame, returnSpot, false);
    return segments;
  });
  if (!noReturn) {
    dynamicPlans.set(activeReturnerRole, {
      team: "defense",
      family: "run",
      scale: touchdown ? 0.96 : tackledReturn ? 0.9 : 0.94,
      sample: "spline",
      delay: 0,
      guide: returnGuide,
    });
  }

  trackPlayer(defenseTracks, decoyReturnerRole, decoyReturnerBase, (basePoint) => {
    const segments: any[] = [];
    const supportPoint = {
      x: forwardReturnX(basePoint.x, returnSpot.x + returnXDelta(34)),
      y: clampRange(
        snapToPixel(targetLaneY + (activeReturnerRole === "RETURNER1" ? 42 : -42)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    addHold(segments, 0, releaseFrame - 1, basePoint, false);
    if (noReturn) {
      addHold(segments, releaseFrame, lastFrame, basePoint, false);
      return segments;
    }
    addPath(segments, releaseFrame, motionEnd, [basePoint, supportPoint], true);
    addHold(segments, motionEnd, lastFrame, supportPoint, false);
    return segments;
  });
  if (!noReturn) {
    const supportPoint = {
      x: forwardReturnX(decoyReturnerBase.x, returnSpot.x + returnXDelta(34)),
      y: clampRange(
        snapToPixel(targetLaneY + (activeReturnerRole === "RETURNER1" ? 42 : -42)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    dynamicPlans.set(decoyReturnerRole, {
      team: "defense",
      family: "run",
      scale: 0.72,
      sample: "spline",
      delay: 4,
      guide: compactPathPoints([decoyReturnerBase, supportPoint]),
      screenOffsetX: 30,
      screenOffsetY: activeReturnerRole === "RETURNER1" ? 42 : -42,
    });
  }

  frontRoles.forEach((role, index) => {
    const basePlayer = findPlayer(defenseBase, role);
    if (!basePlayer) {
      return;
    }
    const matchedCoverageEntry = blockerAssignments.get(role) || coverageEntries[index] || coverageEntries[0] || null;
    const matchedCoverage = matchedCoverageEntry?.player || kickerBase;
    const railPoint = {
      x: forwardReturnX(basePlayer.x, returnSpot.x + returnXDelta(34)),
      y: clampRange(
        snapToPixel(lerp(basePlayer.y, targetLaneY + frontOffsets[index] * 0.55, 0.6)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    const engagePoint = {
      x: forwardReturnX(
        railPoint.x,
        snapToPixel(lerp(railPoint.x, matchedCoverage.x + xDelta(12), 0.28))
      ),
      y: clampRange(
        snapToPixel(lerp(railPoint.y, matchedCoverage.y, 0.32)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    const escortPoint = {
      x: forwardReturnX(engagePoint.x, returnSpot.x + returnXDelta(20)),
      y: clampRange(
        snapToPixel(lerp(engagePoint.y, targetLaneY + frontOffsets[index] * 0.36, 0.28)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };

    trackPlayer(defenseTracks, role, basePlayer, (basePoint) => {
      const segments: any[] = [];
      addHold(segments, 0, releaseFrame - 1, basePoint, false);
      if (noReturn) {
        addHold(segments, releaseFrame, lastFrame, basePoint, false);
        return segments;
      }
      addPath(segments, releaseFrame, motionEnd, [basePoint, railPoint, engagePoint, escortPoint], true);
      addHold(segments, motionEnd, lastFrame, escortPoint, false);
      return segments;
    });
    if (!noReturn) {
      dynamicPlans.set(role, {
        team: "defense",
        family: "run",
        scale: 0.62,
        sample: "spline",
        delay: Math.max(0, 1 + Math.abs(index - 2) - Math.round(hangRatio)),
        guide: compactPathPoints([basePlayer, railPoint, engagePoint, escortPoint]),
        matchRole: matchedCoverageEntry?.role || null,
        screenOffsetX: 28 + Math.abs(index - 2) * 4,
        screenOffsetY: frontOffsets[index] * 0.32,
      });
    }
  });

  wallRoles.forEach((role, index) => {
    const basePlayer = findPlayer(defenseBase, role);
    if (!basePlayer) {
      return;
    }
    const matchedCoverageEntry = blockerAssignments.get(role) || coverageEntries[Math.min(index + frontRoles.length, coverageEntries.length - 1)] || coverageEntries[0] || null;
    const matchedCoverage = matchedCoverageEntry?.player || kickerBase;
    const railPoint = {
      x: forwardReturnX(basePlayer.x, returnSpot.x + returnXDelta(22)),
      y: clampRange(
        snapToPixel(lerp(basePlayer.y, targetLaneY + wallOffsets[index] * 0.58, 0.56)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    const engagePoint = {
      x: forwardReturnX(
        railPoint.x,
        snapToPixel(lerp(railPoint.x, matchedCoverage.x + xDelta(8), 0.24))
      ),
      y: clampRange(
        snapToPixel(lerp(railPoint.y, matchedCoverage.y, 0.26)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    const escortPoint = {
      x: forwardReturnX(engagePoint.x, returnSpot.x + returnXDelta(10)),
      y: clampRange(
        snapToPixel(lerp(engagePoint.y, targetLaneY + wallOffsets[index] * 0.34, 0.24)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };

    trackPlayer(defenseTracks, role, basePlayer, (basePoint) => {
      const segments: any[] = [];
      addHold(segments, 0, releaseFrame - 1, basePoint, false);
      if (noReturn) {
        addHold(segments, releaseFrame, lastFrame, basePoint, false);
        return segments;
      }
      addPath(segments, releaseFrame, motionEnd, [basePoint, railPoint, engagePoint, escortPoint], true);
      addHold(segments, motionEnd, lastFrame, escortPoint, false);
      return segments;
    });
    if (!noReturn) {
      dynamicPlans.set(role, {
        team: "defense",
        family: "run",
        scale: 0.58,
        sample: "spline",
        delay: Math.max(0, 2 + Math.abs(index - 1.5) - Math.round(hangRatio)),
        guide: compactPathPoints([basePlayer, railPoint, engagePoint, escortPoint]),
        matchRole: matchedCoverageEntry?.role || null,
        screenOffsetX: 18 + Math.abs(index - 1.5) * 4,
        screenOffsetY: wallOffsets[index] * 0.3,
      });
    }
  });

  trackPlayer(offenseTracks, "KICKER", kickerBase, (basePoint) => {
    const segments: any[] = [];
    const trailPoint = {
      x: coverageAdvanceX(basePoint.x, basePoint.x + xDelta(12)),
      y: basePoint.y,
    };
    addHold(segments, 0, releaseFrame - 1, basePoint, false);
    addPath(segments, releaseFrame, motionEnd, [basePoint, trailPoint], true);
    addHold(segments, motionEnd, lastFrame, trailPoint, false);
    return segments;
  });
  if (!noReturn) {
    dynamicPlans.set("KICKER", {
      team: "offense",
      family: "run",
      scale: 0.28,
      sample: "polyline",
      delay: 10,
      guide: compactPathPoints([kickerBase, {
        x: coverageAdvanceX(kickerBase.x, returnSpot.x + xDelta(10)),
        y: clampRange(returnSpot.y, metrics.fieldTop + 18, metrics.fieldBottom - 18),
      }]),
    });
  }

  coverageEntries.forEach((entry) => {
    const { role, index, player } = entry;
    const side = player.y < targetLaneY ? -1 : 1;
    const shellPoint = {
      x: coverageAdvanceX(player.x, player.x + xDelta(12 + Math.abs(index - 4.5) * 1.4)),
      y: clampRange(
        snapToPixel(player.y + (index - 4.5) * 2),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    const lanePoint = {
      x: coverageAdvanceX(shellPoint.x, snapToPixel(lerp(shellPoint.x, returnSpot.x + xDelta(26), 0.34))),
      y: clampRange(
        snapToPixel(lerp(shellPoint.y, targetLaneY + (index - 4.5) * 18, 0.26)),
        metrics.fieldTop + 18,
        metrics.fieldBottom - 18
      ),
    };
    const sideOffset = side;
    const approachPoint = role === primaryRole
      ? {
          x: coverageAdvanceX(lanePoint.x, snapToPixel(lerp(lanePoint.x, returnSpot.x, 0.82))),
          y: snapToPixel(lerp(lanePoint.y, returnSpot.y, 0.82)),
        }
      : role === fitRole
        ? {
            x: coverageAdvanceX(lanePoint.x, snapToPixel(lerp(lanePoint.x, returnSpot.x + xDelta(8), 0.78))),
            y: clampRange(returnSpot.y + sideOffset * 14, metrics.fieldTop + 18, metrics.fieldBottom - 18),
          }
        : {
            x: coverageAdvanceX(lanePoint.x, snapToPixel(lerp(lanePoint.x, returnSpot.x + xDelta(10), 0.74))),
            y: clampRange(returnSpot.y + sideOffset * 18, metrics.fieldTop + 18, metrics.fieldBottom - 18),
          };
    const finishPoint = touchdown || outOfBounds
      ? {
          x: snapToPixel(returnSpot.x + xDelta(14)),
          y: clampRange(returnSpot.y + sideOffset * 12, metrics.fieldTop + 18, metrics.fieldBottom - 18),
        }
      : role === primaryRole
        ? { x: returnSpot.x, y: returnSpot.y }
        : {
            x: snapToPixel(returnSpot.x + xDelta(2)),
            y: clampRange(returnSpot.y + sideOffset * 6, metrics.fieldTop + 18, metrics.fieldBottom - 18),
          };
    const containPoint = {
      x: coverageAdvanceX(lanePoint.x, returnSpot.x + xDelta(18)),
      y: clampRange(returnSpot.y + (role === highContainRole ? -34 : 34), metrics.fieldTop + 18, metrics.fieldBottom - 18),
    };
    const trailPoint = {
      x: coverageAdvanceX(lanePoint.x, returnSpot.x + xDelta(12)),
      y: clampRange(targetLaneY + (index - 4.5) * 20, metrics.fieldTop + 18, metrics.fieldBottom - 18),
    };

    trackPlayer(offenseTracks, role, player, (basePoint) => {
      const segments: any[] = [];
      addHold(segments, 0, releaseFrame - 1, basePoint, false);

      if (noReturn) {
        addPath(segments, releaseFrame, motionEnd, [basePoint, shellPoint], true);
        addHold(segments, motionEnd, lastFrame, shellPoint, false);
        return segments;
      }

      if (role === primaryRole || role === fitRole || role === cleanupRole) {
        addPath(segments, releaseFrame, motionEnd, [basePoint, shellPoint, lanePoint, approachPoint, finishPoint], true);
        addHold(segments, motionEnd, lastFrame, finishPoint, false);
        return segments;
      }

      if (role === highContainRole || role === lowContainRole) {
        addPath(segments, releaseFrame, motionEnd, [basePoint, shellPoint, lanePoint, containPoint], true);
        addHold(segments, motionEnd, lastFrame, containPoint, false);
        return segments;
      }

      addPath(segments, releaseFrame, motionEnd, [basePoint, shellPoint, lanePoint, trailPoint], true);
      addHold(segments, motionEnd, lastFrame, trailPoint, false);
      return segments;
    });
    if (!noReturn) {
      dynamicPlans.set(role, {
        team: "offense",
        family: "pass",
        scale: role === primaryRole
          ? 0.82
          : role === fitRole
            ? 0.76
            : role === cleanupRole
              ? 0.7
              : role === highContainRole || role === lowContainRole
                ? 0.64
                : 0.62,
        sample: "spline",
        delay: role === primaryRole
          ? 1
          : role === fitRole
            ? 2
            : role === cleanupRole
              ? 4
              : 3 + Math.abs(index - 4.5) * 0.6,
        guide: compactPathPoints(role === primaryRole || role === fitRole || role === cleanupRole
          ? [player, shellPoint, lanePoint, approachPoint, finishPoint]
          : role === highContainRole || role === lowContainRole
            ? [player, shellPoint, lanePoint, containPoint]
            : [player, shellPoint, lanePoint, trailPoint]),
        blockerRole: coverageMatchByRole.get(role) || null,
        side,
        laneOffsetY: (index - 4.5) * 18,
      });
    }
  });

  const dynamicStates = new Map<string, any>();
  if (!noReturn) {
    dynamicPlans.forEach((plan, role) => {
      const basePlayers = plan.team === "offense" ? offenseBase : defenseBase;
      const tracks = plan.team === "offense" ? offenseTracks : defenseTracks;
      const state = seedMotionState(role, basePlayers, tracks, releaseFrame);
      if (!state) {
        return;
      }
      if (role === activeReturnerRole) {
        state.vx *= 0.26;
        state.vy *= 0.26;
      } else if (role === decoyReturnerRole) {
        state.vx *= 0.34;
        state.vy *= 0.34;
      } else if (role.startsWith("FRONT")) {
        state.vx *= 0.28;
        state.vy *= 0.28;
      } else if (role.startsWith("WALL")) {
        state.vx *= 0.24;
        state.vy *= 0.24;
      } else if (role.startsWith("COVER")) {
        state.vx *= 0.32;
        state.vy *= 0.32;
      } else if (role === "KICKER") {
        state.vx *= 0.2;
        state.vy *= 0.2;
      }
      dynamicStates.set(role, state);
    });
  }

  const applyContest = (
    coverageRole: string,
    blockerRole: string | null | undefined,
    returnProgress: number,
    options: {
      side: number;
      engageDistance: number;
      breakthroughProgress: number;
      holdStrength: number;
      coverageDrag: number;
      blockerDrag: number;
      coverageOffsetX: number;
      coverageOffsetY: number;
      blockerOffsetX: number;
      blockerOffsetY: number;
    },
  ) => {
    if (!blockerRole) {
      return;
    }

    const coverageState = dynamicStates.get(coverageRole);
    const blockerState = dynamicStates.get(blockerRole);
    if (!coverageState || !blockerState) {
      return;
    }

    const gap = pointDistance(coverageState, blockerState);
    if (gap > options.engageDistance) {
      return;
    }

    const progressWindow = clamp01(1 - returnProgress / Math.max(0.08, options.breakthroughProgress));
    const contactBlend = clamp01(1 - gap / Math.max(1, options.engageDistance));
    const contestStrength = progressWindow * options.holdStrength * (0.42 + contactBlend * 0.58);
    if (contestStrength <= 0.02) {
      return;
    }

    const midpoint = {
      x: snapToPixel(lerp(coverageState.x, blockerState.x, 0.5)),
      y: snapToPixel(lerp(coverageState.y, blockerState.y, 0.5)),
    };
    const coverageTarget = {
      x: snapToPixel(midpoint.x + xDelta(options.coverageOffsetX)),
      y: clampRange(midpoint.y + options.side * options.coverageOffsetY, metrics.fieldTop + 18, metrics.fieldBottom - 18),
    };
    const blockerTarget = {
      x: snapToPixel(midpoint.x + returnXDelta(options.blockerOffsetX)),
      y: clampRange(midpoint.y + options.side * options.blockerOffsetY, metrics.fieldTop + 18, metrics.fieldBottom - 18),
    };

    dynamicStates.set(coverageRole, {
      ...coverageState,
      x: snapToPixel(lerp(coverageState.x, coverageTarget.x, contestStrength * 0.36)),
      y: snapToPixel(lerp(coverageState.y, coverageTarget.y, contestStrength * 0.36)),
      vx: lerp(coverageState.vx, blockerState.vx, contestStrength * 0.18) * Math.max(0.52, 1 - contestStrength * options.coverageDrag),
      vy: lerp(coverageState.vy, blockerState.vy, contestStrength * 0.18) * Math.max(0.52, 1 - contestStrength * options.coverageDrag),
    });
    dynamicStates.set(blockerRole, {
      ...blockerState,
      x: snapToPixel(lerp(blockerState.x, blockerTarget.x, contestStrength * 0.34)),
      y: snapToPixel(lerp(blockerState.y, blockerTarget.y, contestStrength * 0.34)),
      vx: lerp(blockerState.vx, coverageState.vx, contestStrength * 0.24) * Math.max(0.72, 1 - contestStrength * options.blockerDrag),
      vy: lerp(blockerState.vy, coverageState.vy, contestStrength * 0.24) * Math.max(0.72, 1 - contestStrength * options.blockerDrag),
    });
  };

  const selectBlockerThreatRole = (blockerRole: string, blockerState: any, carrierPoint: any): string | null => {
    const blockerPlan = dynamicPlans.get(blockerRole);
    if (!blockerPlan || !blockerState || !carrierPoint) {
      return blockerPlan?.matchRole || null;
    }

    const matchedRole = blockerPlan.matchRole || null;
    const targetScreenY = carrierPoint.y + (blockerPlan.screenOffsetY || 0) * 0.72;
    const candidates = coverageRoles
      .map((role) => ({ role, state: dynamicStates.get(role) }))
      .filter((entry) => entry.state)
      .filter((entry) => {
        if (entry.role === matchedRole) {
          return true;
        }
        const toCarrier = pointDistance(entry.state, carrierPoint);
        const laneGapY = Math.abs(entry.state.y - targetScreenY);
        const aheadOfCarrier = (entry.state.x - carrierPoint.x) * returnDirection >= -54;
        return toCarrier <= 252 && laneGapY <= 126 && aheadOfCarrier;
      })
      .sort((left, right) => {
        const leftMatchedBias = left.role === matchedRole ? -140 : 0;
        const rightMatchedBias = right.role === matchedRole ? -140 : 0;
        const leftBreakBias = breakthroughRoles.has(left.role) ? -18 : 0;
        const rightBreakBias = breakthroughRoles.has(right.role) ? -18 : 0;
        const leftScore = leftMatchedBias + leftBreakBias + pointDistance(left.state, blockerState) * 0.96 + pointDistance(left.state, carrierPoint) * 0.64;
        const rightScore = rightMatchedBias + rightBreakBias + pointDistance(right.state, blockerState) * 0.96 + pointDistance(right.state, carrierPoint) * 0.64;
        return leftScore - rightScore;
      });

    return candidates[0]?.role || matchedRole;
  };

  const frames: any[] = [];
  for (let frame = 0; frame <= lastFrame; frame += 1) {
    const offensePlayers = offenseBase.map((player: any) => {
      const next = evaluateTrack(offenseTracks.get(player.role), frame, player);
      return {
        ...player,
        x: next.x,
        y: next.y,
        moving: next.moving,
      };
    });
    const defensePlayers = defenseBase.map((player: any) => {
      const next = evaluateTrack(defenseTracks.get(player.role), frame, player);
      return {
        ...player,
        x: next.x,
        y: next.y,
        moving: next.moving,
      };
    });

    if (!noReturn && frame > releaseFrame) {
      const returnProgress = clamp01((frame - releaseFrame) / Math.max(1, motionEnd - releaseFrame));
      const returnerState = dynamicStates.get(activeReturnerRole);
      const dynamicOrder = [
        activeReturnerRole,
        decoyReturnerRole,
        ...frontRoles,
        ...wallRoles,
        primaryRole,
        fitRole,
        cleanupRole,
        highContainRole,
        lowContainRole,
        ...coverageRoles.filter((role) => ![primaryRole, fitRole, cleanupRole, highContainRole, lowContainRole].includes(role)),
        "KICKER",
      ];

      dynamicOrder.forEach((role) => {
        const plan = dynamicPlans.get(role);
        const state = dynamicStates.get(role);
        if (!plan || !state) {
          return;
        }
        const breakthroughRole = role.startsWith("COVER") && breakthroughRoles.has(role);
        const assignedBlockerState = role.startsWith("COVER") && plan.blockerRole ? dynamicStates.get(plan.blockerRole) : null;

        const progress = clamp01(
          (frame - releaseFrame - (plan.delay || 0)) / Math.max(1, motionEnd - releaseFrame - (plan.delay || 0))
        );
        const guideDriftSign = role === activeReturnerRole
          ? laneBias
          : role === decoyReturnerRole
            ? Math.sign(plan.screenOffsetY || laneBias || 1)
            : Math.sign(plan.screenOffsetY || plan.laneOffsetY || plan.side || laneBias || 1);
        let desired = flowingGuideTarget(plan.guide, progress, plan.sample || "spline", {
          lookahead: role === activeReturnerRole ? 0.084 : role.startsWith("COVER") ? 0.066 : 0.058,
          settleBlend: role === activeReturnerRole ? 0.68 : 0.58,
          driftSign: guideDriftSign,
          driftScale: role === activeReturnerRole ? 0.82 : role.startsWith("FRONT") || role.startsWith("WALL") ? 0.44 : 0.28,
          minDrift: role === activeReturnerRole ? 0.6 : 0.25,
          maxDrift: role === activeReturnerRole ? 3.8 : role.startsWith("FRONT") || role.startsWith("WALL") ? 2.6 : 2,
          nearDriftDistance: 8,
          farDriftDistance: role === activeReturnerRole ? 34 : 24,
        });
        const projectedCarrier = returnerState ? projectedPoint(returnerState, role.startsWith("COVER") ? 4 : 2) : returnSpot;

        if (role === activeReturnerRole) {
          desired = blendPoints(desired, returnSpot, progress > 0.76 ? 0.14 : 0.06);
        } else if (role === decoyReturnerRole) {
          desired = blendPoints(desired, {
            x: forwardReturnX(state.x, projectedCarrier.x + returnXDelta(plan.screenOffsetX || 30)),
            y: clampRange(projectedCarrier.y + (plan.screenOffsetY || 0), metrics.fieldTop + 18, metrics.fieldBottom - 18),
          }, progress > 0.54 ? 0.2 : 0.1);
        } else if (role.startsWith("FRONT") || role.startsWith("WALL")) {
          const guardRole = selectBlockerThreatRole(role, state, projectedCarrier) || plan.matchRole || null;
          const guardState = guardRole ? dynamicStates.get(guardRole) : null;
          plan.activeThreatRole = guardRole;
          const screenTarget = guardState
            ? {
                x: snapToPixel(lerp(
                  projectedCarrier.x + returnXDelta(plan.screenOffsetX || 22),
                  guardState.x + returnXDelta(8),
                  0.44,
                )),
                y: clampRange(lerp(projectedCarrier.y + (plan.screenOffsetY || 0), guardState.y, 0.58), metrics.fieldTop + 18, metrics.fieldBottom - 18),
              }
            : {
                x: projectedCarrier.x + returnXDelta(plan.screenOffsetX || 22),
                y: clampRange(projectedCarrier.y + (plan.screenOffsetY || 0), metrics.fieldTop + 18, metrics.fieldBottom - 18),
              };
          desired = blendPoints(desired, screenTarget, progress > 0.48 ? 0.26 : 0.12);
          if (guardState) {
            const threatToCarrier = pointDistance(guardState, projectedCarrier);
            const threatToBlocker = pointDistance(guardState, state);
            const guardPressure = clamp01((228 - threatToCarrier) / 228) * 0.58
              + clamp01((96 - threatToBlocker) / 96) * 0.42;
            const guardTarget = {
              x: snapToPixel(lerp(
                projectedCarrier.x + returnXDelta(Math.max(10, (plan.screenOffsetX || 22) * 0.72)),
                guardState.x + returnXDelta(10),
                breakthroughRoles.has(guardRole || "") ? 0.62 : 0.54,
              )),
              y: clampRange(
                lerp(projectedCarrier.y + (plan.screenOffsetY || 0) * 0.72, guardState.y, breakthroughRoles.has(guardRole || "") ? 0.7 : 0.62),
                metrics.fieldTop + 18,
                metrics.fieldBottom - 18,
              ),
            };
            desired = blendPoints(
              desired,
              guardTarget,
              0.18 + guardPressure * (breakthroughRoles.has(guardRole || "") ? 0.46 : 0.34),
            );

            if (threatToBlocker <= 86 || threatToCarrier <= 188) {
              const mirrorTarget = {
                x: snapToPixel(guardState.x + returnXDelta(role.startsWith("FRONT") ? 14 : 10)),
                y: clampRange(
                  lerp(guardState.y, projectedCarrier.y + (plan.screenOffsetY || 0) * 0.54, 0.24),
                  metrics.fieldTop + 18,
                  metrics.fieldBottom - 18,
                ),
              };
              desired = blendPoints(desired, mirrorTarget, 0.1 + guardPressure * 0.24);
            }
          }
        } else if (role === primaryRole) {
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + xDelta(16)),
            y: clampRange(projectedCarrier.y + plan.side * 2, metrics.fieldTop + 18, metrics.fieldBottom - 18),
          }, progress > 0.7 ? 0.42 : 0.16);
        } else if (role === fitRole) {
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + xDelta(8)),
            y: clampRange(projectedCarrier.y + plan.side * 14, metrics.fieldTop + 18, metrics.fieldBottom - 18),
          }, progress > 0.72 ? 0.3 : 0.14);
        } else if (role === cleanupRole) {
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + xDelta(2)),
            y: clampRange(projectedCarrier.y + plan.side * 18, metrics.fieldTop + 18, metrics.fieldBottom - 18),
          }, progress > 0.76 ? 0.22 : 0.1);
        } else if (role === highContainRole || role === lowContainRole) {
          desired = blendPoints(desired, {
            x: snapToPixel(projectedCarrier.x + xDelta(18)),
            y: clampRange(projectedCarrier.y + (role === highContainRole ? -32 : 32), metrics.fieldTop + 18, metrics.fieldBottom - 18),
          }, progress > 0.62 ? 0.2 : 0.1);
          if (!breakthroughRole) {
            const containStackTarget = {
              x: snapToPixel(projectedCarrier.x + xDelta(-18)),
              y: clampRange(projectedCarrier.y + (role === highContainRole ? -44 : 44), metrics.fieldTop + 18, metrics.fieldBottom - 18),
            };
            desired = blendPoints(desired, containStackTarget, progress > 0.66 ? 0.34 : 0.18);
          }
        } else if (role.startsWith("COVER")) {
          const chaseTarget = breakthroughRole
            ? {
                x: snapToPixel(projectedCarrier.x + xDelta(12)),
                y: clampRange(projectedCarrier.y + (plan.laneOffsetY || 0) * 0.86, metrics.fieldTop + 18, metrics.fieldBottom - 18),
              }
            : assignedBlockerState
              ? {
                  x: snapToPixel(lerp(
                    assignedBlockerState.x + xDelta(6),
                    projectedCarrier.x + xDelta(-22 - Math.min(18, Math.abs(plan.laneOffsetY || 0) * 0.12)),
                    0.22,
                  )),
                  y: clampRange(
                    lerp(
                      assignedBlockerState.y,
                      projectedCarrier.y + (plan.laneOffsetY || 0) * 0.62,
                      0.26,
                    ),
                    metrics.fieldTop + 18,
                    metrics.fieldBottom - 18,
                  ),
                }
              : {
                  x: snapToPixel(projectedCarrier.x + xDelta(-26 - Math.min(14, Math.abs(plan.laneOffsetY || 0) * 0.1))),
                  y: clampRange(projectedCarrier.y + (plan.laneOffsetY || 0) * 0.72, metrics.fieldTop + 18, metrics.fieldBottom - 18),
                };
          desired = blendPoints(desired, chaseTarget, breakthroughRole
            ? progress > 0.6 ? 0.16 : 0.08
            : progress > 0.62 ? 0.32 : 0.16);

          if (!breakthroughRole && assignedBlockerState) {
            const stackTarget = {
              x: snapToPixel(assignedBlockerState.x + xDelta(10)),
              y: clampRange(
                lerp(assignedBlockerState.y, projectedCarrier.y + (plan.laneOffsetY || 0) * 0.54, 0.16),
                metrics.fieldTop + 18,
                metrics.fieldBottom - 18,
              ),
            };
            desired = blendPoints(desired, stackTarget, progress > 0.72 ? 0.42 : 0.2);
          }
        }

        if (role !== activeReturnerRole && role !== "KICKER") {
          const lateralSign = role === decoyReturnerRole
            ? Math.sign(plan.screenOffsetY || laneBias || 1)
            : role.startsWith("FRONT") || role.startsWith("WALL")
              ? Math.sign(plan.screenOffsetY || (state.y - projectedCarrier.y) || laneBias || 1)
              : role === primaryRole || role === fitRole || role === cleanupRole
                ? Math.sign(plan.side || laneBias || 1)
                : role === highContainRole
                  ? -1
                  : role === lowContainRole
                    ? 1
                    : role.startsWith("COVER")
                      ? Math.sign(plan.laneOffsetY || plan.side || (assignedBlockerState ? assignedBlockerState.y - projectedCarrier.y : laneBias || 1))
                      : 0;
          if (lateralSign !== 0) {
            desired = curvedApproachTarget(state, desired, lateralSign, {
              nearDistance: role.startsWith("FRONT") || role.startsWith("WALL") ? 18 : 22,
              farDistance: role.startsWith("COVER") ? 112 : 94,
              minOffset: role.startsWith("COVER") ? 4 : 3,
              maxOffset: role.startsWith("COVER")
                ? breakthroughRole ? 16 : 13
                : role.startsWith("FRONT") ? 14 : role.startsWith("WALL") ? 12 : 10,
              scale: role === decoyReturnerRole ? 0.76 : role.startsWith("FRONT") || role.startsWith("WALL") ? 0.92 : 1,
            });
          }
        }

        steerMotionState(state, desired, {
          family: plan.family,
          scale: plan.scale,
          accelRatio: role === activeReturnerRole
            ? 0.26
            : role.startsWith("COVER")
              ? breakthroughRole ? 0.22 : 0.16
              : role.startsWith("FRONT") || role.startsWith("WALL")
                ? 0.18
                : 0.16,
          drag: role === activeReturnerRole
            ? 0.9
            : role.startsWith("COVER")
              ? breakthroughRole ? 0.88 : 0.92
              : 0.91,
          inertia: role === activeReturnerRole
            ? 0.986
            : role.startsWith("COVER")
              ? breakthroughRole ? 0.962 : 0.948
              : 0.97,
          arriveDistance: role === activeReturnerRole
            ? 20
            : role.startsWith("COVER")
              ? breakthroughRole ? 22 : 30
              : 18,
          stopDistance: role === activeReturnerRole ? 4 : 5,
        });
      });

      [...frontRoles, ...wallRoles].forEach((blockerRole) => {
        const blockerState = dynamicStates.get(blockerRole);
        const projectedCarrier = returnerState ? projectedPoint(returnerState, 2) : returnSpot;
        const threatRole = blockerState ? (dynamicPlans.get(blockerRole)?.activeThreatRole || selectBlockerThreatRole(blockerRole, blockerState, projectedCarrier)) : null;
        if (!threatRole || !blockerState) {
          return;
        }
        const breakthroughRole = breakthroughRoles.has(threatRole);
        applyContest(threatRole, blockerRole, returnProgress, {
          side: blockerState.y < targetLaneY ? -1 : 1,
          engageDistance: blockerRole.startsWith("FRONT")
            ? breakthroughRole ? 42 : 56
            : breakthroughRole ? 38 : 50,
          breakthroughProgress: blockerRole.startsWith("FRONT")
            ? breakthroughRole ? 0.56 : 1.18
            : breakthroughRole ? 0.66 : 1.24,
          holdStrength: blockerRole.startsWith("FRONT")
            ? breakthroughRole ? 0.84 : 1.3
            : breakthroughRole ? 0.92 : 1.36,
          coverageDrag: blockerRole.startsWith("FRONT")
            ? breakthroughRole ? 0.68 : 0.94
            : breakthroughRole ? 0.74 : 0.96,
          blockerDrag: blockerRole.startsWith("FRONT")
            ? breakthroughRole ? 0.12 : 0.08
            : breakthroughRole ? 0.1 : 0.06,
          coverageOffsetX: breakthroughRole ? 4 : 0,
          coverageOffsetY: blockerRole.startsWith("FRONT") ? 8 : 6,
          blockerOffsetX: blockerRole.startsWith("FRONT")
            ? breakthroughRole ? 14 : 18
            : breakthroughRole ? 12 : 16,
          blockerOffsetY: blockerRole.startsWith("FRONT") ? 8 : 6,
        });
      });

      dynamicPlans.forEach((plan, role) => {
        const state = dynamicStates.get(role);
        if (!state) {
          return;
        }
        setPlayerState(plan.team === "offense" ? offensePlayers : defensePlayers, role, {
          x: state.x,
          y: state.y,
          moving: Math.hypot(state.vx || 0, state.vy || 0) > 0.16,
        });
      });
    }

    const returner = findPlayer(defensePlayers, activeReturnerRole) || activeReturnerBase;
    let ballX;
    let ballY;

    if (frame <= flightEnd) {
      const ballProgress = clamp01(frame / Math.max(1, flightEnd));
      const flightTarget = deadShort
        ? { x: deadBallX + 6, y: context.centerY + 8 }
        : onsideKick
          ? { x: deadBallX + 6, y: context.centerY + 8 }
        : kickoffOutcome === "touchback-35"
          ? { x: directTouchbackCatchPoint.x + 6, y: directTouchbackCatchPoint.y + 8 }
          : kickoffOutcome === "landing-zone-roll-touchback"
            ? { x: rollTouchbackBouncePoint.x + 6, y: rollTouchbackBouncePoint.y + 8 }
          : { x: catchPoint.x + 6, y: catchPoint.y + 8 };
      const flightLift = onsideKick ? 18 : 116 + hangRatio * 18;
      ballX = snapToPixel(lerp(kickoffOrigin.x, flightTarget.x, ballProgress));
      ballY = snapToPixel(lerp(kickoffOrigin.y, flightTarget.y, ballProgress) - Math.sin(ballProgress * Math.PI) * flightLift);
    } else if (kickoffOutcome === "touchback-35") {
      const carryBall = carriedBallPoint(returner, directTouchbackKneelPoint.x, directTouchbackKneelPoint.y, -context.direction);
      ballX = carryBall.ballX;
      ballY = carryBall.ballY;
    } else if (kickoffOutcome === "landing-zone-roll-touchback") {
      const rollProgress = clamp01((frame - flightEnd) / Math.max(1, motionEnd - flightEnd));
      const bouncePoint = guidePointAt(rollTouchbackGuide, rollProgress, "polyline");
      ballX = snapToPixel(bouncePoint.x + 6);
      ballY = snapToPixel(
        bouncePoint.y + 8
        - diminishingBounceLift(rollProgress)
      );
    } else if (deadShort) {
      ballX = deadBallX + 6;
      ballY = context.centerY + 8;
    } else if (onsideKick) {
      ballX = deadBallX + 6;
      ballY = context.centerY + 8;
    } else {
      const carryBall = carriedBallPoint(returner, catchPoint.x, catchPoint.y, -context.direction);
      ballX = carryBall.ballX;
      ballY = carryBall.ballY;
    }

    if (frame > motionEnd) {
      setPlayersStill(offensePlayers, defensePlayers);
    }

    frames.push({
      offensePlayers,
      defensePlayers,
      ball: { ballX, ballY },
    });
  }

  return frames;
}
